import { AfterViewInit, Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { fadeInRightAnimation } from '../../../@fury/animations/fade-in-right.animation';
import { scaleInAnimation } from '../../../@fury/animations/scale-in.animation';
import { fadeInUpAnimation } from '../../../@fury/animations/fade-in-up.animation';
import { Subject } from 'rxjs';
import { FiltersService } from '../../services/base/filters.service';
import { takeUntil } from 'rxjs/operators';
import { HeadCategory } from '../../interfaces/routering/head-category';
import { SubCategory } from '../../interfaces/routering/sub-category';
import { IntegratedOptions } from '../../interfaces/base/integrated-options';
import { WebformService } from '../../services/reports/webform.service';
import { Router } from '@angular/router';
import { RemovableAttachmentComponent } from '../../../@fury/shared/removable-attachment/removable-attachment.component';
import { ReportCreated } from '../../interfaces/report/report-created';
import { LayoutService } from '../../layout/layout.service';
import { AuthenticationService } from '../../services/base/authentication.service';
import { Title } from '@angular/platform-browser';
import { Organisation } from '../../interfaces/routering/organisation';

@Component({
  selector: 'fury-normal-form-control',
  templateUrl: './control.component.html',
  styleUrls: ['./control.component.scss'],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation]
})
export class ControlComponent implements OnInit, AfterViewInit, OnDestroy {

  public filters = {
    'headCategories': {
      preload: true,
      isLoading: false,
      data: <HeadCategory[]>[]
    },
    'subCategories': {
      preload: false,
      isLoading: false,
      data: <SubCategory[]>[]
    },
    'personPrefix': {
      preload: true,
      isLoading: false,
      data: <IntegratedOptions[]>[]
    },
  };

  @ViewChild('attachmentsPlaceholder', {read: ViewContainerRef}) attachmentsPlaceholder: ViewContainerRef;

  public formSubmitting: boolean = false;
  public formSubmitError: boolean = false;
  public formSubmitErrorSubmitted: boolean = false;

  public uploadProgress: number = 0;

  private pageTitle: string = 'Controle melding';

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private filtersService: FiltersService,
              private router: Router,
              private layoutService: LayoutService,
              private titleService: Title,
              public webformService: WebformService,
              public authenticationService: AuthenticationService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle + ' - Straatbeeld Online');
    this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
      if (organisation && typeof organisation.display_name !== 'undefined') {
        this.titleService.setTitle(this.pageTitle + ' - ' + organisation.display_name);
      }
    });

    this.layoutService.isPublicNotificationVisible.emit(false);

    if (this.webformService.checkForm === false) {
      this.router.navigateByUrl('/').then((): void => {
      });
    } else {
      this.filtersService.filters = this.filters;
      this.layoutService.isPublicNotificationVisible.emit(true);

      Object.keys(this.filters).map((filter: string): void => {
        if (this.filters[filter].preload) {
          this.filtersService[filter]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
            if (data) {
              this.filters[filter].data = data;
            }
          });
        }
      });

      if (this.webformService.normalForm.controls.head_category_id.value && this.webformService.normalForm.controls.sub_category_id.value) {
        this.filtersService.subCategories(this.webformService.normalForm.controls.head_category_id.value).pipe(takeUntil(this.destroy$)).subscribe((response: SubCategory[] | false): void => {
          this.filters.subCategories.isLoading = false;
          if (response) {
            this.filters.subCategories.data = response;
          }
        });
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.webformService.checkForm === true) {
      if (typeof this.webformService.normalForm.controls.attachments !== 'undefined') {
        if (this.webformService.normalForm.controls.attachments.value) {
          Object.keys(this.webformService.normalForm.controls.attachments.value).map((d: string): void => {
            const component: ComponentRef<RemovableAttachmentComponent> = this.attachmentsPlaceholder.createComponent(RemovableAttachmentComponent),
              file = this.webformService.normalForm.controls.attachments.value[d];
            component.instance.filename = file.name;
            component.instance.size = file.size;
            component.instance.removable = false;
          });
        }
      }
    }
  }

  editForm(section: string): void {
    this.router.navigateByUrl('#' + section).then(() => {
    });
  }

  getTextValue(attribute: string, id: number): string {
    if (typeof this.filters[attribute].data !== 'undefined') {
      const index: number = this.filters[attribute].data.findIndex((d: any): boolean => d.id === parseInt(<string><unknown>id, 10));

      if (index !== -1) {
        return this.filters[attribute].data[index].name;
      }
    }

    return null;
  }

  submit(): void {
    this.formSubmitting = true;
    this.formSubmitError = false;
    this.layoutService.scrollToTop.next();

    this.webformService.save(this.webformService.normalForm.value).subscribe((value: number | ReportCreated | boolean): void => {
      if (typeof value === 'number') {
        this.uploadProgress = value;
      } else if (value === false) {
        if (this.webformService.hasValidErrors) {
          this.router.navigate(['/']).then((): void => {
          });
        } else {
          this.formSubmitting = false;
          this.formSubmitError = true;
        }
      } else {
        this.router.navigate(['/vastgelegd/' + (<ReportCreated>value).unique_number]).then((): void => {
        });
      }
    });
  }

  failedReport(): void {
    this.formSubmitting = true;
    this.formSubmitErrorSubmitted = false;
    this.webformService.forwardFailedReport(this.webformService.normalForm.value).subscribe((value: boolean): void => {
      this.formSubmitting = false;
      if (value) {
        this.formSubmitError = false;
        this.formSubmitErrorSubmitted = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
